import React, { useState, useEffect, useRef, useCallback } from 'react';
import useDimensions from '../../hooks/useDimensions';
import { useNavigate } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import LoadingPage from '../LoadingPage';
import { getColors } from '../../Constants';
import './SvgChart.css';
import PartyList from './PartyList';

const FULL_RADIUS = 180;

interface MandatData {
    pocet_mandatu: number;
    strana_barva: string;
    strana_id: number;
    strana_zkratka?: string;
}

type SvgChartProps = {
    basicData: {
        nejnovejsi_zasedani?: {
            mandaty: Array<{
                pocet_mandatu: number;
                strana_barva: string;
                strana_id: number;
            }>;
            legalni_mandaty: Array<{
                pocet_mandatu: number;
                strana_barva: string;
                strana_id: number;
            }>;
        };
        koalice: Array<{ strana_zkratka: string }>;
    };
    partiesData: any[];
    legalPartiesData: any[];
    coalitionView?: boolean;
    epView?: boolean;
    politicalView: string;
};

type ChartDot = {
    id: number;
    x: number;
    y: number;
    angleDeg: number;
    color: string;
    satColor?: string;
    partyId: number;
    colored: boolean;
}

const SvgChart = ({ basicData, partiesData, legalPartiesData, coalitionView, epView, politicalView }: SvgChartProps) => {
    const { width: containerWidth } = useDimensions();
    const navigate = useNavigate();
    const colors = getColors();

    const radiusGenerator = (sum: number, width: number) => {
        if (sum <= 10) return width / 3;
        if (sum <= 15) return width / 5;
        if (sum < 25) return width / 4;
        return width / 5;
    };

    const dotSpaceCalculator = (dotSize: number) => dotSize * 1.2;

    const sizeCalculator = (sum: number, radius: number, width: number, offset: number) => {
        let dot_size = 1;
        let dot_space = 0;
        let perimeter = 0;
        let row_count = 1;
        let current_radius = radius;

        while (true) {
            dot_space = dotSpaceCalculator(dot_size);
            perimeter = Math.PI * current_radius;
            let space = perimeter / (sum - row_count);

            while (space < dot_space) {
                row_count += 1;
                current_radius = current_radius + dot_space;
                perimeter += Math.PI * current_radius;
                space = perimeter / (sum - row_count);
            }

            if (dot_size >= width / 15 || current_radius + dot_space >= width * 0.47 - offset) {
                break;
            } else {
                dot_size += 1;
                row_count = 1;
                current_radius = radius;
            }
        }

        // For small diagrams
        if (sum <= 5) dot_size = width / 8;
        else if (sum <= 7) dot_size = width / 10;
        else if (sum <= 10) dot_size = width / 12;
        else if (sum <= 15) dot_size = width / 14;

        let final_perimeter = Math.PI * radius;
        let temp_radius = radius;
        for (let i = 1; i < row_count; i++) {
            temp_radius += dot_space;
            final_perimeter += Math.PI * temp_radius;
        }
        let final_space = final_perimeter / (sum - row_count);

        return { space: final_space, dot_size, dot_space, row_count };
    };

    const generateSvg = () => {
        const zasedani = basicData?.nejnovejsi_zasedani;
        if (!zasedani) return "";

        // Color link
        const memberOriginalColorMap: Record<number, string> = {};
        legalPartiesData.forEach(party => {
            party.clenove.forEach((member: any) => {
                if (member.aktivni === 1) {
                    memberOriginalColorMap[member.id] = party.strana_barva;
                }
            });
        });
        const clubMemberColorsMap: Record<number, string[]> = {};
        partiesData.forEach(club => {
            const sortedActiveMembers = club.clenove
                .filter((m: any) => m.aktivni === 1)
                .sort((a: any, b: any) => {
                    const colorA = memberOriginalColorMap[a.id] || "";
                    const colorB = memberOriginalColorMap[b.id] || "";
                    return colorA.localeCompare(colorB);
                });

            clubMemberColorsMap[club.strana_id] = sortedActiveMembers.map(
                (m: any) => memberOriginalColorMap[m.id] || club.strana_barva
            );
        });

        // Type of political view
        let mandaty: MandatData[] = [];
        if (politicalView === 'parties') {
            mandaty = zasedani.legalni_mandaty || [];
        } else if (politicalView === 'subjects' || politicalView === 'combined') {
            mandaty = zasedani.mandaty || [];
        }

        if (mandaty.length === 0) return "";

        const totalSeats = mandaty.reduce((sum, m) => sum + m.pocet_mandatu, 0);

        const w = containerWidth > 0 ? containerWidth : 500;

        const radius = radiusGenerator(totalSeats, w);
        const { space, dot_size, dot_space, row_count } = sizeCalculator(totalSeats, radius, w, 10);

        const offset = totalSeats > 50 ? dot_size / 6 : dot_size / 2; // Value to make chart in center
        const h = w / 2 + offset + 20;

        const width_center = w / 2;
        const height_center = w / 2 - offset;

        // Positions
        let dots: ChartDot[] = [];
        let dot_remain = 0;
        let current_radius = radius;
        let count = 0;

        for (let i = 0; i < row_count; i++) {
            let perimeter = Math.PI * current_radius;
            let dot_count = perimeter / space + 1 + dot_remain;
            dot_remain = dot_count - Math.floor(dot_count);
            dot_count = Math.floor(dot_count);

            if (i + 1 === row_count) {
                dot_count = Math.round(dot_remain + dot_count);
            }

            const angleStep = FULL_RADIUS / (dot_count - 1);

            for (let j = 0; j < dot_count; j++) {
                count += 1;
                const angleDeg = angleStep * j;
                const angleRad = (angleDeg * Math.PI) / 180;
                
                dots.push({
                    id: count,
                    x: width_center - current_radius * Math.cos(angleRad),
                    y: height_center - current_radius * Math.sin(angleRad),
                    angleDeg: angleDeg,
                    color: "black",
                    partyId: 0,
                    colored: false
                });
            }
            current_radius += dot_space;
        }

        // Colors
        let partyIndex = 0;
        let party_count = 0;

        for (let i = 0; i <= FULL_RADIUS; i++) {
            for (let dot of dots) {
                if (!dot.colored && dot.angleDeg <= i) {
                    if (partyIndex < mandaty.length) {
                        const currentGroup = mandaty[partyIndex];
                    
                        dot.partyId = currentGroup.strana_id;
                        dot.colored = true;
                        
                        if (politicalView === 'combined') {
                            dot.color = currentGroup.strana_barva;
                            
                            const memberSatColors = clubMemberColorsMap[currentGroup.strana_id];
                            
                            if (memberSatColors && memberSatColors[party_count]) {
                                dot.satColor = memberSatColors[party_count];
                            } else {
                                dot.satColor = currentGroup.strana_barva; // Fallback
                            }
                        } else {
                            dot.color = currentGroup.strana_barva;
                            dot.satColor = undefined;
                        }
                        
                        party_count++;

                        if (party_count >= currentGroup.pocet_mandatu) {
                            partyIndex++;
                            party_count = 0;
                        }
                    }
                }
            }
        }

        // Correction
        dots.forEach(dot => {
            if (!dot.colored && mandaty.length > 0) {
                const lastParty = mandaty[mandaty.length - 1];
                dot.color = lastParty.strana_barva;
                dot.partyId = lastParty.strana_id;
                dot.colored = true;
            }
        });

        let content = `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">`;

        const partiesMap: Record<string | number, ChartDot[]> = {};

        dots.forEach(dot => {
            const key = dot.partyId ?? 'unknown'; 
            if (!partiesMap[key]) {
                partiesMap[key] = [];
            }
            partiesMap[key].push(dot);
        });

        // Party dont have page
        const isClickable = politicalView === 'subjects' || politicalView === 'combined';
        const cursorStyle = isClickable ? 'cursor:pointer' : 'cursor:default';
        
        for (const partyId in partiesMap) {
            const dotsHtml = partiesMap[partyId]
            .map(dot => {
                const isCombined = politicalView === 'combined' && !!dot.satColor;
                const fullRadius = dot_size / 2;
                
                if (isCombined) {
                    const mainR = fullRadius * 0.85;
                    const satR = fullRadius * 0.5;
                    const offset = mainR * 0.6; 

                    return `
                        <g style="${cursorStyle}">
                            <ellipse 
                                class="party-${partyId}"
                                cx="${dot.x}" cy="${dot.y}" 
                                ry="${mainR}" rx="${mainR}" 
                                fill="${dot.color}" 
                            />
                            <ellipse 
                                class="party-${partyId}"
                                cx="${dot.x + offset}" cy="${dot.y + offset}" 
                                ry="${satR}" rx="${satR}" 
                                fill="${dot.satColor}" 
                                stroke="white"
                                stroke-width="${satR * 0.25}"
                            />
                        </g>`;
                } else {
                    const r = fullRadius;
                    return `<ellipse 
                        class="party-${partyId}"
                        cx="${dot.x}" cy="${dot.y}" 
                        ry="${r}" rx="${r}"
                        fill="${dot.color}" 
                        style="${cursorStyle}"
                    />`;
                }
            })
            .join('');
            
            content += `<g>${dotsHtml}</g>`;
        }
        content += `</svg>`;

        return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(content)))}`;
    };

    const svgData = generateSvg();

    const [hoveredParties, setHoveredParties] = useState<(string | number)[] | null>(null);
    const svgRef = useRef<SVGElement>(null);

    const handleMouseEnter = useCallback((partyId: string) => {
        let idsToHover: (string | number)[] = [partyId];

        // For combined view find all legal parties associated with this subject
        if (politicalView === 'combined') {
            const club = partiesData.find(p => String(p.strana_id) === String(partyId));
            if (club && club.clenove) {
                const clubMemberIds = new Set(club.clenove.map((m: any) => m.id));

                legalPartiesData.forEach(lp => {
                    if (lp.clenove && lp.clenove.some((m: any) => clubMemberIds.has(m.id))) {
                        idsToHover.push(lp.strana_id);
                    }
                });
            }
        }

        setHoveredParties(idsToHover);
    }, [politicalView, partiesData, legalPartiesData]);
    
    const handleMouseLeave = useCallback(() => {
        setHoveredParties(null);
    }, []);
    
    const handleEllipseClick = useCallback((partyId: string) => {
        if (politicalView === 'parties') return;
        
        navigate(`subjekty/${partyId}`);
    }, [navigate, politicalView]);

    const attachEventListeners = useCallback((svgElement: SVGElement | null) => {
        if (!svgElement) return;
        
        const elements = svgElement.querySelectorAll<SVGEllipseElement>('ellipse[class^="party-"]');

        elements.forEach((el) => {
            const node = el as any;
            const className = node.getAttribute('class');
            if (!className) return;
            
            const partyId = className.split('-')[1];
            
            const mouseenter = () => handleMouseEnter(partyId);
            const mouseleave = () => handleMouseLeave();
            const click = () => handleEllipseClick(partyId);

            node.addEventListener('mouseenter', mouseenter);
            node.addEventListener('mouseleave', mouseleave);
            node.addEventListener('click', click);

            node._eventListeners = { mouseenter, mouseleave, click };
        });

        return () => {
            elements.forEach((el) => {
                const node = el as any;
                if (node._eventListeners) {
                    const { mouseenter, mouseleave, click } = node._eventListeners;
                    node.removeEventListener('mouseenter', mouseenter);
                    node.removeEventListener('mouseleave', mouseleave);
                    node.removeEventListener('click', click);
                }
            });
        };
    }, [handleMouseEnter, handleMouseLeave, handleEllipseClick]);

    // Update table hover
    useEffect(() => {
        let cleanupFn: (() => void) | undefined;
        const timeoutId = setTimeout(() => {
            if (svgRef.current) {
                cleanupFn = attachEventListeners(svgRef.current);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (cleanupFn) cleanupFn();
        };
    }, [svgData, attachEventListeners]);

    const { koalice } = basicData;

    // Data separation
    const getSplitData = (sourceArray: any[]) => {
        const active = sourceArray.filter((p) => p.aktivni);
        
        return {
            coalition: active.filter((p) => 
                koalice.some((c: any) => c.strana_zkratka === p.strana_zkratka)
            ),
            opposition: active.filter((p) => 
                !koalice.some((c: any) => c.strana_zkratka === p.strana_zkratka)
            )
        };
    };

    return (
        <div className='mandate-svg'>
            <div className='item-svg'>
                <SVG
                    innerRef={svgRef}
                    loader={<LoadingPage />}
                    src={svgData}
                />
            </div>

            <div className="party-lists-container">
                {politicalView === 'combined' ? (
                    <>
                        <div className="combined-lists-wrapper">
                            <div className="list-section">
                                <PartyList
                                    coalitionParties={getSplitData(partiesData).coalition}
                                    oppositionParties={getSplitData(partiesData).opposition}
                                    epText={epView}
                                    splitByCoalition={false}
                                    hoveredParties={hoveredParties}
                                    isSubject={true}
                                    colors={colors}
                                    twoLists={true}
                                />
                            </div>
                            
                            <div className="list-section">
                                <PartyList
                                    coalitionParties={getSplitData(legalPartiesData).coalition}
                                    oppositionParties={getSplitData(legalPartiesData).opposition}
                                    epText={epView}
                                    splitByCoalition={false}
                                    hoveredParties={hoveredParties}
                                    isSubject={false}
                                    colors={colors}
                                    twoLists={true}
                                />
                            </div>
                        </div>
                    </>
                ) : (

                    <PartyList
                        coalitionParties={getSplitData(politicalView === 'parties' ? legalPartiesData : partiesData).coalition}
                        oppositionParties={getSplitData(politicalView === 'parties' ? legalPartiesData : partiesData).opposition}
                        epText={epView}
                        splitByCoalition={coalitionView}
                        hoveredParties={hoveredParties}
                        isSubject={politicalView === 'parties' ? false : true}
                        colors={colors}
                    />
                )}
            </div>
        </div>
    );
};

export default SvgChart;
